import {
  isCalendarDay,
  isDayOnly,
  isGroup,
  isZonelessDateTime,
  looksZoneless,
  STORE_TIMEZONE,
  zonelessInstant,
  type Filter,
  type FilterCondition,
  type FilterNode,
  type Json,
} from "@estelle/domain";

/**
 * A row flattened to the field names the module declares.
 *
 * Filters name fields like `customerName` and `shippingProvinceCode`, which
 * are not properties of `Order` — they are joins and nested reads. Each
 * repository supplies a projection so the filter engine never has to know the
 * shape of the thing it is filtering.
 */
export type Projection = Record<string, unknown>;

const DAY = 86_400_000;

/**
 * `"2026-09-09"` — the calendar date `ms` falls on in the store's zone
 * (`STORE_TIMEZONE`), for the calendar-day operators below (#406).
 * `en-CA` formats as `YYYY-MM-DD`, which is both the display and the sort
 * order, so two of these compare correctly with plain string operators —
 * the same trick an ISO date string is chosen for everywhere else in this
 * file. A `new Intl.DateTimeFormat` per call is cheap next to the array scans
 * around it and keeps this a pure function of `ms`, same as `daysBetween`.
 */
function calendarDateKey(ms: number): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: STORE_TIMEZONE }).format(
    new Date(ms),
  );
}

/** `2026-09-30T…`: a stored timestamp, as opposed to a bare day. */
const INSTANT_PREFIX = /^\d{4}-\d{2}-\d{2}T/;

function asNumber(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function asList(value: Json | undefined): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Compare two values the way the field's own type implies.
 *
 * Money and dates both arrive as strings. Money has to compare numerically —
 * `"90" > "1000"` is true as text and wrong as money — while ISO dates
 * compare correctly as text precisely because the format was chosen for that.
 */
function compare(left: unknown, right: Json | undefined): number | null {
  const leftNumber = asNumber(left);
  const rightNumber = asNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }

  // asText, not String(): a filter value can be any Json, and an object
  // reaching a comparison would stringify to "[object Object]" and compare
  // equal to every other object.
  let leftText = asText(left);
  const rightText = asText(right);
  if (leftText === "" || rightText === "") return null;

  // A date typed without a time is a calendar day in the store's zone, so a
  // timestamp is compared as the day it falls on there. As text,
  // "2026-09-30T05:00:00.000Z" sorts after "2026-09-30", which made "between
  // 30 Sep and 30 Sep" empty and "= 30 Sep" match nothing (#492). A day that
  // does not exist, `2026-02-31`, compares with nothing, as the API's SQL does.
  if (isDayOnly(rightText)) {
    if (!isCalendarDay(rightText)) return null;
    if (INSTANT_PREFIX.test(leftText)) {
      const parsed = Date.parse(leftText);
      if (Number.isNaN(parsed)) return null;
      leftText = calendarDateKey(parsed);
    }
  }

  // A time typed without a zone, `2026-09-30T10:00`, is the store's wall clock
  // (#519), as the API's SQL reads it. As text it would be compared against a
  // UTC ISO string, which put "after 10:00" at half past three in Kolkata. The
  // stored value names its own instant, so the two are compared as instants.
  // One that is no time, `2026-02-31T10:00`, compares with nothing.
  if (looksZoneless(rightText)) {
    const wanted = zonelessInstant(rightText);
    const stored = Date.parse(leftText);
    if (wanted === null || Number.isNaN(stored)) return null;
    return stored - wanted;
  }

  return leftText < rightText ? -1 : leftText > rightText ? 1 : 0;
}

/**
 * Whether `actual` falls on the store's calendar day that a time typed with no
 * zone names, for `=` and `≠` (#519). The API reads the same two as the day, not
 * the moment — "= 30 Sep 10:00" on a datetime field has never meant the one
 * instant — and now reads both days in the store's zone.
 *
 * `undefined` when `expected` is not such a time, so the caller compares as it
 * always has.
 */
function sameStoreDay(
  actual: unknown,
  expected: Json | undefined,
): boolean | undefined {
  const wanted = asText(expected);
  if (!isZonelessDateTime(wanted)) return undefined;
  const text = asText(actual);
  const parsed = text === "" ? Number.NaN : Date.parse(text);
  return (
    !Number.isNaN(parsed) && calendarDateKey(parsed) === wanted.slice(0, 10)
  );
}

function daysBetween(value: unknown, now: number): number | null {
  const text = asText(value);
  if (text === "") return null;
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : (now - parsed) / DAY;
}

/**
 * A text operator on a field that holds a list (an SKU list, HSN codes): the
 * API's `listCondition` asks whether a member matches, so `contains "E"` holds
 * when any member contains it and the negative operators when none does
 * (#541). `undefined` for an operator that is not one of the six.
 */
function listText(
  members: unknown[],
  operator: string,
  expected: Json | undefined,
): boolean | undefined {
  const wanted = asText(expected).toLowerCase();
  const texts = members.map((member) => asText(member));
  switch (operator) {
    case "is":
      return texts.includes(asText(expected));
    case "is_not":
      return !texts.includes(asText(expected));
    case "contains":
      return texts.some((text) => text.toLowerCase().includes(wanted));
    case "not_contains":
      return !texts.some((text) => text.toLowerCase().includes(wanted));
    case "starts_with":
      return texts.some((text) => text.toLowerCase().startsWith(wanted));
    case "ends_with":
      return texts.some((text) => text.toLowerCase().endsWith(wanted));
    default:
      return undefined;
  }
}

/**
 * Whether one clause holds for one row.
 *
 * An operator this does not know returns false rather than throwing. A stale
 * saved view naming a removed operator should show an empty list the filter
 * builder can explain, not take the page down.
 */
function matchesCondition(
  projection: Projection,
  condition: FilterCondition,
  now: number,
): boolean {
  const actual = projection[condition.field];
  const expected = condition.value;

  if (Array.isArray(actual)) {
    const listed = listText(actual, condition.operator, expected);
    if (listed !== undefined) return listed;
  }

  switch (condition.operator) {
    case "is_empty":
      return isEmpty(actual);
    case "is_not_empty":
      return !isEmpty(actual);

    case "is":
      return asText(actual) === asText(expected);
    case "is_not":
      return asText(actual) !== asText(expected);
    case "contains":
      return asText(actual)
        .toLowerCase()
        .includes(asText(expected).toLowerCase());
    case "not_contains":
      return !asText(actual)
        .toLowerCase()
        .includes(asText(expected).toLowerCase());
    case "starts_with":
      return asText(actual)
        .toLowerCase()
        .startsWith(asText(expected).toLowerCase());
    case "ends_with":
      return asText(actual)
        .toLowerCase()
        .endsWith(asText(expected).toLowerCase());

    case "eq":
      return sameStoreDay(actual, expected) ?? compare(actual, expected) === 0;
    case "neq": {
      const same = sameStoreDay(actual, expected);
      return same === undefined ? compare(actual, expected) !== 0 : !same;
    }
    case "lt": {
      const result = compare(actual, expected);
      return result !== null && result < 0;
    }
    case "lte": {
      const result = compare(actual, expected);
      return result !== null && result <= 0;
    }
    case "gt": {
      const result = compare(actual, expected);
      return result !== null && result > 0;
    }
    case "gte": {
      const result = compare(actual, expected);
      return result !== null && result >= 0;
    }
    case "between": {
      const [low, high] = asList(expected);
      const lower = compare(actual, low as Json);
      const upper = compare(actual, high as Json);
      return lower !== null && upper !== null && lower >= 0 && upper <= 0;
    }

    case "in_last_days": {
      const age = daysBetween(actual, now);
      const window = asNumber(expected);
      return age !== null && window !== null && age >= 0 && age <= window;
    }
    case "in_next_days": {
      const age = daysBetween(actual, now);
      const window = asNumber(expected);
      return age !== null && window !== null && age < 0 && -age <= window;
    }

    // Calendar-day operators (#406): compared as the store's own calendar
    // date, not a rolling window of hours from `now` — see `calendarDateKey`.
    case "is_today": {
      const text = asText(actual);
      const parsed = text === "" ? NaN : Date.parse(text);
      return (
        !Number.isNaN(parsed) &&
        calendarDateKey(parsed) === calendarDateKey(now)
      );
    }
    case "in_last_calendar_days": {
      const text = asText(actual);
      const parsed = text === "" ? NaN : Date.parse(text);
      const window = asNumber(expected);
      if (Number.isNaN(parsed) || window === null || window < 1) return false;
      const rowKey = calendarDateKey(parsed);
      const todayKey = calendarDateKey(now);
      const startKey = calendarDateKey(now - (window - 1) * DAY);
      return rowKey >= startKey && rowKey <= todayKey;
    }

    case "is_any_of":
      return asList(expected).some((item) => asText(item) === asText(actual));
    case "is_none_of":
      return !asList(expected).some((item) => asText(item) === asText(actual));

    case "has_tag":
      return (
        Array.isArray(actual) &&
        actual.some((item) => asText(item) === asText(expected))
      );
    case "not_has_tag":
      return !(
        Array.isArray(actual) &&
        actual.some((item) => asText(item) === asText(expected))
      );
    case "has_any_tag":
      return (
        Array.isArray(actual) &&
        asList(expected).some((wanted) =>
          actual.some((item) => asText(item) === asText(wanted)),
        )
      );

    case "is_true":
      return actual === true;
    case "is_false":
      return actual === false;

    default:
      return false;
  }
}

function matchesNode(
  projection: Projection,
  node: FilterNode,
  now: number,
): boolean {
  if (!isGroup(node)) return matchesCondition(projection, node, now);

  // An empty group matches everything. It is the state a filter starts in,
  // and "no conditions" has to mean "no narrowing" or an empty filter would
  // hide every row.
  if (node.children.length === 0) return true;

  return node.combinator === "and"
    ? node.children.every((child) => matchesNode(projection, child, now))
    : node.children.some((child) => matchesNode(projection, child, now));
}

/** Whether a row survives the filter. */
export function matchesFilter(
  projection: Projection,
  filter: Filter | undefined,
  now: number,
): boolean {
  if (!filter) return true;
  return matchesNode(projection, filter, now);
}
